/**
 * Intelligent Nutrient Calculator
 *
 * Calculates personalized nutrient recommendations based on:
 * - Sex (biological differences in requirements)
 * - Weight (body mass-dependent nutrients)
 * - Calories (energy-dependent vitamins)
 * - Activity Level (metabolic demands)
 * - Age (life stage requirements)
 *
 * These are BASELINE recommendations. Therapeutic targets set by coaches override these.
 */

interface UserProfile {
  gender: string;
  age: number;
  weight: number; // kg
  height: number; // cm
  activity_level: string;
  daily_calorie_rec?: number;
  primary_focus?: string;
}

interface NutrientRecommendations {
  // Macronutrients
  daily_calorie_rec: number;
  protein_rec: number;
  carbs_rec: number;
  fat_rec: number;
  fiber_rec: number;

  // Vitamins
  vitamin_a_rec: number;
  vitamin_c_rec: number;
  vitamin_d_rec: number;
  vitamin_e_rec: number;
  vitamin_k_rec: number;
  thiamin_rec: number;
  riboflavin_rec: number;
  niacin_rec: number;
  vitamin_b6_rec: number;
  folate_rec: number;
  vitamin_b12_rec: number;

  // Minerals
  calcium_rec: number;
  iron_rec: number;
  magnesium_rec: number;
  phosphorus_rec: number;
  potassium_rec: number;
  sodium_rec: number;
  zinc_rec: number;
  selenium_rec: number;

  // Essential Fatty Acids
  omega3_rec: number;
  omega6_rec: number;

  // Essential Amino Acids (mg/day)
  lysine_rec: number;
  leucine_rec: number;
  isoleucine_rec: number;
  phenylalanine_rec: number;
  methionine_rec: number;
  valine_rec: number;
  threonine_rec: number;
  tryptophan_rec: number;
  histidine_rec: number;
}

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 */
function calculateTDEE(profile: UserProfile): number {
  const { gender, age, weight, height, activity_level } = profile;

  // Calculate BMR using Harris-Benedict Equation
  let bmr: number;
  if (gender === 'male') {
    bmr = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
  } else {
    bmr = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
  }

  // Activity multipliers
  const activityMultipliers: { [key: string]: number } = {
    sedentary: 1.2,
    lightly_active: 1.375,
    moderately_active: 1.55,
    very_active: 1.725,
    extremely_active: 1.9
  };

  const multiplier = activityMultipliers[activity_level] || 1.55;
  return Math.round(bmr * multiplier);
}

/**
 * Adjust TDEE based on primary goal
 * Uses percentage-based approach for personalized, sustainable calorie targets
 */
function adjustCaloriesForGoal(tdee: number, primaryFocus: string, gender?: string): number {
  let adjustedCalories: number;

  switch (primaryFocus) {
    case 'weight-loss':
    case 'weight_loss':
      // 15% deficit for sustainable weight loss (~0.5kg/week for most users)
      // Percentage-based approach scales appropriately for different TDEE levels
      adjustedCalories = Math.round(tdee * 0.85);

      // Safety floor: Prevent dangerously low calorie intake
      const minimumCalories = gender === 'male' ? 1500 : 1200;
      return Math.max(adjustedCalories, minimumCalories);

    case 'muscle-gain':
    case 'muscle_gain':
      // 300 kcal surplus for lean muscle gain
      return tdee + 300;

    case 'health-general':
    case 'maintenance':
    default:
      // Maintenance calories
      return tdee;
  }
}

/**
 * Calculate macronutrient recommendations including essential fatty acids
 */
function calculateMacros(profile: UserProfile, tdee: number): Partial<NutrientRecommendations> {
  const { weight, primary_focus, activity_level, gender } = profile;

  // Adjust calories based on goal FIRST
  const adjustedCalories = adjustCaloriesForGoal(tdee, primary_focus || '', gender);

  // Protein: 1.0-2.0g/kg based on activity level
  let proteinPerKg = 1.0; // sedentary baseline

  if (activity_level === 'lightly_active') {
    proteinPerKg = 1.25;
  } else if (activity_level === 'moderately_active') {
    proteinPerKg = 1.5;
  } else if (activity_level === 'very_active') {
    proteinPerKg = 1.75;
  } else if (activity_level === 'extremely_active') {
    proteinPerKg = 2.0;
  }

  // Muscle gain goal increases by 0.2g/kg
  if (primary_focus === 'muscle_gain' || primary_focus === 'muscle-gain') {
    proteinPerKg += 0.2;
  }

  const protein = Math.round(weight * proteinPerKg);

  // Fat: 25-35% of calories, aim for 30% (based on adjusted calories)
  const fatCalories = adjustedCalories * 0.30;
  const fat = Math.round(fatCalories / 9); // 9 cal/g

  // Essential Fats: 20% of overall fat intake
  const essentialFats = fat * 0.20;

  // Omega-3: 25% of essential fats
  const omega3 = Number((essentialFats * 0.25).toFixed(1));

  // Omega-6: 75% of essential fats
  const omega6 = Number((essentialFats * 0.75).toFixed(1));

  // Carbs: Remainder after protein and fat (based on adjusted calories)
  const proteinCalories = protein * 4;
  const carbCalories = adjustedCalories - proteinCalories - fatCalories;
  const carbs = Math.round(carbCalories / 4);

  // Fiber: 14g per 1000 kcal (based on adjusted calories)
  const fiber = Math.round((adjustedCalories / 1000) * 14);

  return {
    daily_calorie_rec: adjustedCalories,
    protein_rec: protein,
    carbs_rec: carbs,
    fat_rec: fat,
    fiber_rec: fiber,
    omega3_rec: omega3,
    omega6_rec: omega6
  };
}

/**
 * Calculate sex-dependent mineral recommendations
 */
function calculateSexDependentNutrients(profile: UserProfile): Partial<NutrientRecommendations> {
  const { gender, age } = profile;
  const isMale = gender === 'male';
  const isFemaleChildbearing = !isMale && age >= 15 && age <= 50;

  return {
    // Iron: 8mg male, 15mg female (childbearing age)
    iron_rec: isFemaleChildbearing ? 15 : 8,

    // Zinc: 11mg male, 8mg female
    zinc_rec: isMale ? 11 : 8,

    // Selenium: 75mcg male, 60mcg female
    selenium_rec: isMale ? 75 : 60
  };
}

/**
 * Calculate calorie-dependent vitamins (B-complex)
 * These scale with energy intake to support metabolism
 */
function calculateCalorieDependentVitamins(calories: number): Partial<NutrientRecommendations> {
  const per1000kcal = calories / 1000;

  return {
    // Thiamin (B1): 0.4mg per 1000 kcal
    thiamin_rec: Number((per1000kcal * 0.4).toFixed(1)),

    // Riboflavin (B2): 1.2mg per 1000 kcal
    riboflavin_rec: Number((per1000kcal * 1.2).toFixed(1)),

    // Niacin (B3): 13mg (fixed amount)
    niacin_rec: 13
  };
}

/**
 * Calculate weight-dependent nutrients
 */
function calculateWeightDependentNutrients(weight: number): Partial<NutrientRecommendations> {
  return {
    // Magnesium: 5.5mg/kg
    magnesium_rec: Math.round(weight * 5.5),

    // Potassium: 50mg/kg (minimum 3500mg)
    potassium_rec: Math.max(3500, Math.round(weight * 50)),

    // Phosphorus: 12mg/kg
    phosphorus_rec: Math.round(weight * 12)
  };
}

/**
 * Calculate essential amino acid requirements
 * Based on WHO/FAO recommendations: mg/kg bodyweight
 */
function calculateAminoAcids(weight: number): Partial<NutrientRecommendations> {
  return {
    lysine_rec: Math.round(weight * 12),
    leucine_rec: Math.round(weight * 14),
    isoleucine_rec: Math.round(weight * 10),
    phenylalanine_rec: Math.round(weight * 14),
    methionine_rec: Math.round(weight * 13),
    valine_rec: Math.round(weight * 10),
    threonine_rec: Math.round(weight * 7),
    tryptophan_rec: Number((weight * 3.5).toFixed(1)),
    histidine_rec: Math.round(weight * 10)
  };
}

/**
 * Calculate protein-dependent vitamins
 */
function calculateProteinDependentVitamins(proteinGrams: number): Partial<NutrientRecommendations> {
  return {
    // Vitamin B6: 15mcg per gram of protein
    vitamin_b6_rec: Number((proteinGrams * 0.015).toFixed(1))
  };
}

/**
 * Calculate standard and sex-specific vitamin recommendations
 */
function calculateStandardVitamins(profile: UserProfile, weight: number): Partial<NutrientRecommendations> {
  const { gender } = profile;
  const isMale = gender === 'male';

  return {
    // Vitamin A (mcg RAE) - 700 male, 600 female
    vitamin_a_rec: isMale ? 700 : 600,

    // Vitamin C (mg) - 80mg standard
    vitamin_c_rec: 80,

    // Vitamin D (IU) - 400 IU
    vitamin_d_rec: 400,

    // Vitamin E (mg) - 7mg standard
    vitamin_e_rec: 7,

    // Vitamin K (mcg) - 1mcg per kg bodyweight
    vitamin_k_rec: Math.round(weight * 1),

    // Vitamin B12 (mcg) - 1.5mcg
    vitamin_b12_rec: 1.5,

    // Folic Acid (mcg) - 3.1mcg per kg bodyweight
    folate_rec: Number((weight * 3.1).toFixed(1)),

    // Fixed minerals
    // Calcium (mg) - 800mg
    calcium_rec: 800,

    // Sodium (mg) - 2000mg (2g)
    sodium_rec: 2000
  };
}

/**
 * Main calculation function
 * Combines all calculation methods to produce complete recommendations
 */
export function calculateNutrientRecommendations(profile: UserProfile): NutrientRecommendations {
  // Calculate TDEE (maintenance calories)
  const tdee = calculateTDEE(profile);

  // Calculate macros with goal-adjusted calories
  const macros = calculateMacros(profile, tdee);
  const proteinGrams = typeof macros.protein_rec === 'number' ? macros.protein_rec : 0;

  // Use adjusted calories for calorie-dependent vitamins
  const adjustedCalories = typeof macros.daily_calorie_rec === 'number' ? macros.daily_calorie_rec : tdee;

  const sexDependent = calculateSexDependentNutrients(profile);
  const calorieDependent = calculateCalorieDependentVitamins(adjustedCalories);
  const proteinDependent = calculateProteinDependentVitamins(proteinGrams);
  const weightDependent = calculateWeightDependentNutrients(profile.weight);
  const aminoAcids = calculateAminoAcids(profile.weight);
  const standardVitamins = calculateStandardVitamins(profile, profile.weight);

  // Combine all calculations
  return {
    ...macros,
    ...standardVitamins,
    ...sexDependent,
    ...calorieDependent,
    ...proteinDependent,
    ...weightDependent,
    ...aminoAcids
  } as NutrientRecommendations;
}

/**
 * Helper function to check if profile has minimum required data
 */
export function canCalculateRecommendations(profile: Partial<UserProfile>): boolean {
  return !!(
    profile.gender &&
    profile.age &&
    profile.weight &&
    profile.height &&
    profile.activity_level
  );
}
